import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { changeUserSettingsAsync } from "../../../redux/user/userActions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

function SettingsView() {
  const { t, i18n } = useTranslation();
  const languages = useSelector((state) => state.layout.LgDropDownLinks);
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const loading = useSelector((state) => state.user.loading);

  const preferredLang = user?.currentUser?.user?.userPreferences?.language;
  const [ready, setReady] = useState(false);

  const [settings, setSettings] = useState({
    language: preferredLang || i18n.language || "en",
    notificationType: {
      inForbiddenZone: true,
      outStayZone: false,
      speeding: true,
    },
    notificationChannel: {
      inApp: true,
      email: false,
      sms: false,
    },
  });

  const initialSettings = useRef(settings);
  const justUpdatedRef = useRef(false);

  useEffect(() => {
    if (user?.currentUser?.user?.userPreferences) {
      const {
        language,
        notificationType,
        notificationChannel,
      } = user.currentUser.user.userPreferences;

      if (justUpdatedRef.current) {
        justUpdatedRef.current = false;
        return; // Skip reset if we just saved
      }

      setSettings({
        language,
        notificationType: { ...notificationType },
        notificationChannel: { ...notificationChannel },
      });

      initialSettings.current = {
        language,
        notificationType: { ...notificationType },
        notificationChannel: { ...notificationChannel },
      };

      if (language && i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    }
    setReady(true);
  }, [user, i18n]);

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  const handleToggle = (section, field) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field],
      },
    }));
  };

  const handleSave = () => {
    if (user) {
      const userId = user.currentUser.user._id;
      const token = user.currentUser.token;
      justUpdatedRef.current = true;
      dispatch(changeUserSettingsAsync(userId, settings, token));
    }
  };

  const isModified = JSON.stringify(settings) !== JSON.stringify(initialSettings.current);
  
  if (!ready) return null;
  
  return (
    <div className="max-w-2xl mx-auto p-8 mt-4">
      <Card className="bg-blue-50 bg-opacity-30">
        <CardContent className="p-8">
          {/* Language Section */}
          <div className="border-b border-gray-200 border-opacity-50 pb-8 mb-8 flex justify-between items-start">
            <div className="flex-1 pr-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("settings.language")}
              </h3>
              <p className="text-sm text-gray-600">
                {t("settings.languageDes")}
              </p>
            </div>
            <div className="w-48">
              <Select
                value={i18n.language}
                onValueChange={(value) => {
                  changeLanguage(value);
                  setSettings({ ...settings, language: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map(({ abrrev, title }) => (
                    <SelectItem key={title} value={abrrev}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subscription Section */}
          <div className="border-b border-gray-200 border-opacity-50 pb-8 mb-8 flex justify-between items-start">
            <div className="flex-1 pr-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("settings.subscription")}
              </h3>
              <p className="text-sm text-gray-600">
                {t("settings.subscriptionDes")}
              </p>
            </div>
            <div className="w-48">
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                {t("settings.subscriptionBtn")}
              </Button>
            </div>
          </div>

          {/* Notification Type Section */}
          <div className="border-b border-gray-200 border-opacity-50 pb-8 mb-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1 pr-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("settings.notificationType")}
                </h3>
                <p className="text-sm text-gray-600">
                  {t("settings.notificationTypeDes")}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.inForbiddenZone")}
                </span>
                <Switch
                  checked={settings.notificationType.inForbiddenZone}
                  onCheckedChange={() => handleToggle("notificationType", "inForbiddenZone")}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.outStayZone")}
                </span>
                <Switch
                  checked={settings.notificationType.outStayZone}
                  onCheckedChange={() => handleToggle("notificationType", "outStayZone")}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.speeding")}
                </span>
                <Switch
                  checked={settings.notificationType.speeding}
                  onCheckedChange={() => handleToggle("notificationType", "speeding")}
                />
              </div>
            </div>
          </div>

          {/* Notification Channel Section */}
          <div className="border-b border-gray-200 border-opacity-50 pb-8 mb-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1 pr-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("settings.notificationChannel")}
                </h3>
                <p className="text-sm text-gray-600">
                  {t("settings.notificationChannelDes")}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.inApp")}
                </span>
                <Switch
                  checked={settings.notificationChannel.inApp}
                  onCheckedChange={() => handleToggle("notificationChannel", "inApp")}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.email")}
                </span>
                <Switch
                  checked={settings.notificationChannel.email}
                  onCheckedChange={() => handleToggle("notificationChannel", "email")}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t("settings.sms")}
                </span>
                <Switch
                  checked={settings.notificationChannel.sms}
                  onCheckedChange={() => handleToggle("notificationChannel", "sms")}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          {isModified && (
            <div className="flex justify-end">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 px-8"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  t("settings.save")
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsView; 